import React from 'react';
import { Sheet, Typography, Button, Grid } from '@mui/joy';
import { fbAnalytics } from '../../../utils/FBAnalytics.ts';

// Facebook Pixel Events Configuration
interface PixelEvent {
  id: string;
  name: string;
  description: string;
  action: () => void;
  color?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
}

const FACEBOOK_PIXEL_EVENTS: PixelEvent[] = [
  {
    id: 'subscribe',
    name: 'Subscribe',
    description: 'Track subscription events',
    action: () => fbAnalytics.trackSubscribe(),
    color: 'success'
  },
  {
    id: 'trial_started',
    name: 'Trial Started',
    description: 'Track when users start a free trial',
    action: () => fbAnalytics.trackStartTrial(),
    color: 'primary'
  },
  {
    id: 'page_view',
    name: 'Page View',
    description: 'Track page view events',
    action: () => fbAnalytics.trackPageView(),
    color: 'neutral'
  },
  {
    id: 'view_content',
    name: 'View Content',
    description: 'Track content view events',
    action: () => fbAnalytics.trackViewContent(),
    color: 'primary'
  },
  {
    id: 'complete_registration',
    name: 'Complete Registration',
    description: 'Track registration completion events',
    action: () => fbAnalytics.trackCompleteRegistration(),
    color: 'success'
  }
];

// Facebook Pixel Events Testing Component
export const FacebookPixelEventsCard = () => {
  const [trackingResults, setTrackingResults] = React.useState<Record<string, string>>({});

  // Facebook Pixel tracking function using fbAnalytics
  const trackPixelEvent = (event: PixelEvent) => {
    try {
      // Execute the event action
      event.action();
      
      setTrackingResults(prev => ({
        ...prev,
        [event.id]: `✅ Successfully tracked ${event.name} event`
      }));
      
      // Clear the result after 3 seconds
      setTimeout(() => {
        setTrackingResults(prev => {
          const newResults = { ...prev };
          delete newResults[event.id];
          return newResults;
        });
      }, 3000);
    } catch (error) {
      setTrackingResults(prev => ({
        ...prev,
        [event.id]: `❌ Error tracking event: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
      
      // Clear the error after 5 seconds
      setTimeout(() => {
        setTrackingResults(prev => {
          const newResults = { ...prev };
          delete newResults[event.id];
          return newResults;
        });
      }, 5000);
    }
  };

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
      <Typography level="title-lg" sx={{ mb: 2 }}>Facebook Pixel Events Testing</Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
        Test Facebook Pixel events by clicking the buttons below. Events are tracked using fbAnalytics.
      </Typography>
      
      <Grid container spacing={2}>
        {FACEBOOK_PIXEL_EVENTS.map((event) => (
          <Grid xs={12} sm={6} md={4} key={event.id}>
            <Sheet 
              variant="soft" 
              sx={{ 
                p: 2, 
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'neutral.200',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography level="title-sm">{event.name}</Typography>
              <Typography level="body-sm" color="neutral">
                {event.description}
              </Typography>
              <Button
                variant="solid"
                color={event.color || 'primary'}
                onClick={() => trackPixelEvent(event)}
                sx={{ mt: 'auto' }}
                fullWidth
              >
                Track {event.name}
              </Button>
              {trackingResults[event.id] && (
                <Typography 
                  level="body-xs" 
                  color={trackingResults[event.id].startsWith('✅') ? 'success' : 'danger'}
                  sx={{ mt: 1, fontFamily: 'monospace' }}
                >
                  {trackingResults[event.id]}
                </Typography>
              )}
            </Sheet>
          </Grid>
        ))}
      </Grid>
      
      <Typography level="body-xs" color="neutral" sx={{ mt: 3, fontStyle: 'italic' }}>
        💡 Events are now using the built-in fbAnalytics methods with proper error handling and default parameters.
      </Typography>
    </Sheet>
  );
};
